/**
 * Waiting for the card's own faces before measuring anything.
 *
 * ⚠️ **`document.fonts.ready` IS NOT ENOUGH, AND LOOKS LIKE IT IS.** It resolves
 * once nothing is loading. At the card's first render nothing has asked for
 * `Uniform` yet - a face downloads only when something is drawn in it - so
 * `ready` resolves at once, the fit measures in the fallback face, and the real
 * one swaps in under a measurement that no longer holds. So the card names every
 * face it needs and asks for each one with `document.fonts.load`, and fits only
 * after that settles.
 *
 * ⚠️ **IT NEVER HANGS AND NEVER THROWS.** A face that fails to load leaves the
 * card in the fallback face, which is ugly and still a card; a card that never
 * appears is not. The timeout is the same lesson as the capture's frame wait
 * (L65): a promise the browser owns can simply never settle.
 */

/** What a field without a family of its own inherits. Set on the card's root too. */
export const CARD_DEFAULT_FAMILY = "LINESeedSansTH";

/** Long enough for a phone on event wifi; short enough that a dead font is not a dead card. */
const LOAD_TIMEOUT_MS = 5_000;

export interface FontUse {
  fontFamily?: string | undefined;
  fontWeight?: string | undefined;
  text?: string | undefined;
}

/**
 * The `font` shorthands one card needs, one per distinct family and weight, each
 * with the characters drawn in it. The size is irrelevant to which file loads.
 *
 * The real characters are passed because `fonts.load` loads only the faces
 * whose range covers the sample - so the sample should be what will be drawn.
 */
export function fontRequestsOf(fields: readonly FontUse[]): { font: string; text: string }[] {
  const byFont = new Map<string, Set<string>>();
  for (const field of fields) {
    const family = field.fontFamily ?? CARD_DEFAULT_FAMILY;
    const weight = field.fontWeight ?? "normal";
    const font = `${weight} 16px "${family}"`;
    const characters = byFont.get(font) ?? new Set<string>();
    for (const character of field.text ?? "") characters.add(character);
    byFont.set(font, characters);
  }
  return [...byFont].map(([font, characters]) => ({ font, text: [...characters].join("") }));
}

/** Resolves when every face the card needs has loaded, failed, or run out of time. */
export function loadCardFonts(fields: readonly FontUse[]): Promise<void> {
  const fonts = typeof document === "undefined" ? undefined : document.fonts;
  if (fonts === undefined) return Promise.resolve();
  const loads = fontRequestsOf(fields).map(({ font, text }) =>
    // An empty sample would ask for the space glyph only; any Latin letter makes
    // the browser fetch the face the field will actually draw in.
    fonts.load(font, text === "" ? "A" : text).catch(() => []),
  );
  const timeout = new Promise<void>((resolve) => window.setTimeout(resolve, LOAD_TIMEOUT_MS));
  return Promise.race([Promise.all(loads).then(() => undefined), timeout]);
}
